/**
 * Monthly invoice series in the Scheduled-dashboard Forecast style: clean
 * axes, short month ticks, compact £ scale, currency tooltip, and a
 * click-to-drill bar. Defaults to the rolling 12 months of data with an
 * "All history" toggle.
 */
import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { type DrillMonth } from './MonthDrilldown';

interface MonthlySpendChartProps {
  rows: { name: string; value?: number }[];
  /** £ formatter shared with the page. */
  formatValue: (value: number) => string;
  onBarClick?: (month: DrillMonth) => void;
}

export default function MonthlySpendChart({ rows, formatValue, onBarClick }: MonthlySpendChartProps) {
  // Default to the rolling year — five years of history is context, not the
  // working view. Full history stays one click away.
  const [range, setRange] = useState<'12m' | 'all'>('12m');
  const windowed = useMemo(
    () => (range === '12m' ? rows.slice(-12) : rows),
    [rows, range],
  );
  const chartData = useMemo(() => windowed.map((r) => {
    const d = new Date(r.name);
    const valid = !Number.isNaN(d.getTime());
    return {
      iso: r.name,
      label: valid
        ? d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
        : r.name,
      fullLabel: valid
        ? d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
        : r.name,
      value: r.value || 0,
    };
  }), [windowed]);

  if (chartData.length === 0) {
    return (
      <div className="flex h-72 items-center justify-center text-sm text-muted-foreground">
        No invoiced spend in the current view scope.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-2 flex justify-end">
        <div className="flex rounded-md border border-slate-200 p-0.5">
          {([
            ['12m', 'Last 12 months'],
            ['all', 'All history'],
          ] as const).map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              onClick={() => setRange(mode)}
              className={`rounded px-2 py-1 text-[11px] font-medium transition-colors ${
                range === mode
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-900 hover:bg-slate-100'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 5, right: 16, bottom: 0, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: '#0f172a' }}
            axisLine={false}
            tickLine={false}
            minTickGap={24}
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#0f172a' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: number) => formatValue(v)}
            width={56}
          />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 6 }}
            labelFormatter={(label, items) => {
              const p = items?.[0]?.payload as { fullLabel?: string } | undefined;
              return p?.fullLabel ?? String(label);
            }}
            formatter={(value) => [formatValue(Number(value) || 0), 'Invoiced spend']}
          />
          {/* Animation off: the main Dashboard re-renders while settings and
              dashboard data stream in, and each re-render restarts recharts'
              mount animation — bars froze at a few percent of their height. */}
          <Bar dataKey="value" fill="#3b82f6" name="Invoiced spend" radius={[3, 3, 0, 0]}
               isAnimationActive={false}
               cursor={onBarClick ? 'pointer' : 'default'}
               onClick={(row: unknown) => {
                 const p = row as { iso?: string; fullLabel?: string; value?: number };
                 if (onBarClick && p?.iso) {
                   onBarClick({ iso: p.iso, label: p.fullLabel || p.iso, value: p.value || 0 });
                 }
               }} />
        </BarChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
}

